import { IS_HIDE_TAURI_UPDATER_ENABLED } from '@/app-shell/flags/desktop-flags';
import {
  trackTauriFailedToRelaunch,
  trackTauriRelaunch,
  trackTauriUpdate,
} from '@/lib/analytics/scribe/tauri';
import { store } from '@/store';
import { systemController } from '@/tauri/controller/system';
import { updaterBridge, UpdaterBridgeError } from '@/tauri/updater';
import { beginUpdateCheck, isLatestUpdateCheck } from '@/tauri/updater/check-sequence';
import {
  checkUpdateForFeed,
  installCachedForFeed,
  installUpdateForFeed,
  recordOfferForFeed,
  resolveUpdateDelivery,
  resolveUpdaterFeed,
  type OfferedDelivery,
  type UpdateDelivery,
  type UpdaterFeed,
} from '@/tauri/updater/feed';
import {
  listenToUpdaterPrefetchState,
  type PrefetchState,
  type UpdaterPrefetchStatePayload,
} from '@/tauri/updater/prefetch-events';
import {
  prefetchRegionForFeed,
  stateDescribesOffer,
  unarmedDescribesOffer,
} from '@/tauri/updater/prompt-gate';
import { updateFailureMessages } from '@/tauri/updater/updater.messages';
import { isTauri } from '@/tauri/utils';
import { isConfirmedDesktopOfflineNetworkMode } from '@/utils/http/desktopOfflineNetwork';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { formatMessageOutsideReact } from '@heidi/runtime/i18n';
import { type Region } from '@heidi/schema/ml-scribe';
import { createModel } from '@rematch/core';
import { getVersion } from '@tauri-apps/api/app';
import { relaunch } from '@tauri-apps/plugin-process';
import { type MessageDescriptor } from 'react-intl';
import { type RootModel } from '.';
// import { RecordingStatus } from '../scribeModel';

/** Undefined when the error did not come through an updater bridge. */
const updaterErrorCode = (error: unknown): string | undefined =>
  error instanceof UpdaterBridgeError ? error.errorCode : undefined;

/**
 * Bounded log context for a caught error. Never the message: plugin errors can
 * embed install paths, which carry the OS username. An unrecognised error logs
 * its message length instead, so different failures can still be told apart.
 */
const updaterErrorContext = (error: unknown) => {
  const errorCode = updaterErrorCode(error);
  const errorName = error instanceof Error ? error.name : typeof error;
  if (errorCode !== undefined && errorCode !== 'UPDATER_UNCLASSIFIED') {
    return { errorCode, errorName };
  }
  const message = error instanceof Error ? error.message : String(error);
  return { errorCode, errorName, messageLength: message.length };
};

/**
 * Failures that retrying the same version cannot fix: every retry re-downloads
 * the whole bundle and fails the same way. The offer is dropped and that
 * version is not offered again this session (`rejected_update` is not
 * persisted, see `tauriUpdateTransform` in `src/store.ts`).
 */
const NON_RETRYABLE_INSTALL_CODES = new Set([
  'UPDATER_SIGNATURE_INVALID',
  'UPDATER_SIGNED_VERSION_MISSING',
  'UPDATER_SIGNED_VERSION_MISMATCH',
  'UPDATER_PREVIOUS_APP_NOT_RESTORED',
]);

/**
 * Refusals that mean the work is already happening natively: a prefetch holds
 * the download gate, or an install is running. Not failures — the offer stays
 * and the same button installs once the collision clears — so they are logged.
 */
const ALREADY_IN_FLIGHT_INSTALL_CODES = new Set([
  'UPDATER_DOWNLOAD_IN_FLIGHT',
  'UPDATER_INSTALL_IN_FLIGHT',
]);

/**
 * Codes with a remedy the user owns (unblock a network, get an admin to install)
 * get bespoke copy, and failures a retry cannot fix point at the website
 * download or a reinstall. Everything else (malformed manifest, missing platform
 * asset, unclassified) is ours to fix, so it gets generic retry copy.
 */
const installFailureBody = (errorCode: string | undefined): MessageDescriptor => {
  switch (errorCode) {
    // A Rust-side deadline surfaces as a reqwest failure, i.e. UPDATER_NETWORK —
    // there is no separate frontend timeout code to handle.
    case 'UPDATER_NETWORK':
      return updateFailureMessages.installNetworkFailed;
    case 'UPDATER_INSTALL_PERMISSION_DENIED':
      return updateFailureMessages.installPermissionDenied;
    case 'UPDATER_SIGNATURE_INVALID':
    case 'UPDATER_SIGNED_VERSION_MISSING':
    case 'UPDATER_SIGNED_VERSION_MISMATCH':
      return updateFailureMessages.installVerificationFailed;
    case 'UPDATER_PREVIOUS_APP_NOT_RESTORED':
      return updateFailureMessages.installPreviousAppNotRestored;
    // Explicit, not defaulted: a cached install carries this code when the
    // feed was unreachable, so the one copy it must never get is the one
    // blaming the update server. Retrying is honest — the bytes are on disk.
    case 'UPDATER_INSTALL_IO':
      return updateFailureMessages.installFailed;
    default:
      return updateFailureMessages.installFailed;
  }
};

// todo use tanstack's states

export enum UploadStatus {
  Queued = 'queued',
  Uploading = 'uploading',
  Completed = 'completed',
  Failed = 'failed',
}

/**
 * How far the background download of the offered bundle has got. Owned by Rust
 * and mirrored here: on the cached path `ready` installs the bytes on disk and
 * `failed` leaves the offer in place and installs by downloading.
 */
export type { PrefetchState };

export type InitialState = {
  current_version?: string;
  cpu_arch?: string;
  /** A version whose install failed non-retryably; not offered again this session. */
  rejected_update?: { version: string; errorCode: string };
  /**
   * A mirror of the native prefetch state; the state-event handler is its only
   * writer. Deliberately NOT persisted (`tauriUpdateTransform` in
   * `src/store.ts`): a rehydrated `ready` can name a bundle already pruned.
   */
  prefetch_state: PrefetchState;
  /**
   * The version `prefetch_state` describes, without which a state left over
   * from a superseded offer reads as an answer about the current one.
   */
  prefetch_version?: string;
  /**
   * The feed `prefetch_state` was established on, absent for the global feed.
   * Mirrored beside the version because the offer's identity is both: a
   * readiness on one channel says nothing about another channel's bytes.
   */
  prefetch_region?: Region;
  /** Why a `failed` prefetch failed, as the native side classified it. */
  prefetch_reason?: string;
  /**
   * How long the download that produced the ready bytes took. Mirrored for
   * display; the analytics event takes its copy from the install's own report.
   */
  prefetch_ms?: number;
  /**
   * A version whose prefetch this renderer could not arm, so no state event will
   * ever describe it. The one field here that is NOT a mirror — the native side
   * cannot know — and it is what keeps the restart prompt reachable.
   */
  prefetch_unarmed_version?: string;
  /**
   * The feed that un-armable offer was on, absent for the global feed. Beside
   * the version for the same reason `prefetch_region` is: the pair is the
   * offer's identity, so a note about one channel answers for nothing else.
   */
  prefetch_unarmed_region?: Region;
  version_to_update?: {
    version: string;
    body?: string;
    isInstalled: boolean;
    popup_dismissed: boolean;
    // The feed the check resolved. Install reuses it instead of re-reading
    // the gate, so a flag flip between check and install can't reroute a UK/EU
    // client that checked the certified channel onto the global feed.
    feed: UpdaterFeed;
    // The delivery path the LAST check resolved, and a check is its only
    // writer: the install reuses it rather than reading the gate again. Not
    // persisted, so a rehydrated offer reads `unresolved` until this session's
    // first check — which keeps the restart prompt shut until then.
    delivery: OfferedDelivery;
  };
};

const initialState: InitialState = { prefetch_state: 'idle' };

export const tauriUpdateModel = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setCurrentVersion(state, payload: string) {
      return { ...state, current_version: payload };
    },
    setCpuArch(state, payload?: string) {
      return { ...state, cpu_arch: payload };
    },
    setVersionToUpdate(state, payload: InitialState['version_to_update']) {
      return { ...state, version_to_update: payload };
    },
    setRejectedUpdate(state, payload: NonNullable<InitialState['rejected_update']>) {
      return { ...state, rejected_update: payload };
    },
    /**
     * Mirror the native prefetch state. The whole payload lands at once —
     * there is no reducer that can set one field of it — because the native
     * side publishes one state and a pair where half is stale says nothing.
     */
    applyPrefetchState(state, payload: UpdaterPrefetchStatePayload) {
      // A native state supersedes the renderer's own "could not arm" note, but
      // only when it is about the SAME offer: the prefetch evidently did reach
      // the coordinator after all. Version alone is not an offer's identity, so
      // an event for one channel must not clear another channel's note and
      // leave the prompt waiting on an event nobody will publish.
      const clearsUnarmed =
        state.prefetch_unarmed_version === payload.version &&
        state.prefetch_unarmed_region === payload.region;
      return {
        ...state,
        prefetch_state: payload.state,
        prefetch_version: payload.version,
        prefetch_region: payload.region,
        prefetch_reason: payload.reason,
        prefetch_ms: payload.prefetchMs,
        prefetch_unarmed_version: clearsUnarmed
          ? undefined
          : state.prefetch_unarmed_version,
        prefetch_unarmed_region: clearsUnarmed
          ? undefined
          : state.prefetch_unarmed_region,
      };
    },
    /**
     * Note that no prefetch could be armed for `payload`, so the prompt opens
     * on the offer and installs by downloading. Carries the feed as well as the
     * version because that pair is the offer's identity, the same rule a
     * readiness is read by. Set only where the renderer itself failed; every
     * other state comes from the native side.
     */
    setPrefetchUnarmed(state, payload: { version: string; region?: Region }) {
      return {
        ...state,
        prefetch_unarmed_version: payload.version,
        prefetch_unarmed_region: payload.region,
      };
    },
    clear() {
      return initialState;
    },
  },
  effects: (dispatch) => ({
    async getCurrentVersion() {
      if (!isTauri()) {
        return;
      }
      try {
        dispatch.tauriUpdateModel.setCurrentVersion(await getVersion());
      } catch (error) {
        logger.warn('Error reading app version', updaterErrorContext(error));
      }
    },
    async getCpuArchOnce() {
      if (!isTauri()) {
        return;
      }
      if (store.getState().tauriUpdateModel.cpu_arch) return;
      try {
        const arch = await systemController.get_cpu_architecture();
        dispatch.tauriUpdateModel.setCpuArch(arch);
      } catch (error) {
        logger.error('Error fetching CPU arch', updaterErrorContext(error));
        dispatch.tauriUpdateModel.setCpuArch(undefined);
      }
    },
    /**
     * `userInitiated` distinguishes the "Check for updates" button from the
     * background check that fires on mount and on session change. Only the
     * former may raise a toast — surfacing a background failure would interrupt
     * a clinician mid-consult over something they never asked for.
     */
    async handleCheckTauriUpdate(payload?: { userInitiated?: boolean }) {
      const userInitiated = payload?.userInitiated ?? false;
      // Held outside the try so the catch can ask whether this failed check is
      // still the one that owns the offer. Undefined when the throw beat the
      // gate reading, in which case this check never superseded anything.
      let checkToken: number | undefined;
      // Held outside the try for the recovery below, which must answer with the
      // path THIS check resolved. Undefined when the throw beat the gate
      // reading, and that is not the same as `legacy`: a path nobody read can
      // neither arm cached work nor rewrite the offer away from it.
      let delivery: UpdateDelivery | undefined;
      try {
        if (!isTauri()) {
          return;
        }

        if (isConfirmedDesktopOfflineNetworkMode()) {
          logger.warn('[updater] offline - skipping update check');
          return;
        }

        // The E2E harness drives a pinned build; an "Install & relaunch" toast
        // mid-run only overlays the UI under test.
        if (
          !userInitiated &&
          typeof window !== 'undefined' &&
          window.__IS_E2E__ === true
        ) {
          logger.info('[updater] e2e run - skipping background update check');
          return;
        }

        // Taken before either gate is read, so tokens are issued in the order
        // the gates were read. Everything written after an await is conditional
        // on still holding the latest: an earlier reading cannot undo a later.
        checkToken = beginUpdateCheck();

        // The fail-closed routing decision lives in `resolveUpdaterFeed`
        // — see `src/tauri/updater/feed.ts`.
        const feed = resolveUpdaterFeed();
        if (feed.kind === 'blocked') {
          // Fail-closed: do NO update activity, not even a native call to drop
          // the record, and clear the stale offer so a UK/EU user is never
          // shown a possibly uncertified version.

          // Clearing the offer is what shuts the prompt, which is gated on an
          // offer and a readiness naming the same version. Re-runs once the
          // provider or region resolves.
          logger.warn('[updater] gate/region unresolved — skipping update check');
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
          return;
        }

        // Read ONCE per check and carried on the offer, which keeps the install
        // off a second read of the gate. A flip lands on the next check, and
        // that is the point: turning it off reroutes an offer recorded while on.
        delivery = resolveUpdateDelivery();

        const update = await checkUpdateForFeed(feed, delivery);
        if (!isLatestUpdateCheck(checkToken)) {
          // A later check has read the gate since this one did, so it owns the
          // offer. Writing it, dropping it, or recording a prefetch here would
          // all answer with a gate reading the newer check has superseded.
          logger.info('[updater] superseded check - discarding its result', {
            tag: 'updater_check_superseded',
            version: update?.version,
            userInitiated,
          });
          return;
        }
        const { version_to_update: versionToUpdate, rejected_update: rejectedUpdate } =
          store.getState().tauriUpdateModel;
        if (
          update &&
          rejectedUpdate !== undefined &&
          update.version === rejectedUpdate.version
        ) {
          // Logged so a suppressed client can be told apart from an up-to-date one.
          logger.warn('[updater] suppressed a version that failed to install', {
            tag: 'updater_rejected_version_suppressed',
            version: update.version,
            errorCode: rejectedUpdate.errorCode,
            userInitiated,
          });
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
          // Nothing will ever install this version, so the native side must
          // stop holding it as the offer it re-evaluates.
          await dispatch.tauriUpdateModel.releaseUpdateOffer();
          if (userInitiated) {
            toast({
              tone: 'destructive',
              title: formatMessageOutsideReact(updateFailureMessages.installFailedTitle),
              description: formatMessageOutsideReact(
                installFailureBody(rejectedUpdate.errorCode)
              ),
            });
          }
          return;
        }
        if (update) {
          // Offer deduplication: re-recording the same version would reset
          // the dismissal and the body for no reason.
          const retainedOffer =
            versionToUpdate?.version === update.version ? versionToUpdate : undefined;
          if (!retainedOffer) {
            dispatch.tauriUpdateModel.setVersionToUpdate({
              version: update.version,
              body: update.body,
              popup_dismissed: false,
              isInstalled: false,
              feed, // thread the resolved feed through to install
              delivery, // and the path its install takes
            });
          } else if (retainedOffer.delivery !== delivery) {
            // A retained offer takes the path THIS check resolved, whatever it
            // was carrying: keeping the recorded one would leave the gate
            // unable to rescue the client whose prefetch is the broken thing.
            dispatch.tauriUpdateModel.setVersionToUpdate({
              ...retainedOffer,
              delivery,
            });
          }
          if (delivery === 'legacy') {
            // No prefetch on this path: the prompt opens on the offer and the
            // click downloads, as before the cache existed. The native record
            // is dropped only when the mirror says there is one to drop.
            if (store.getState().tauriUpdateModel.prefetch_state !== 'idle') {
              await dispatch.tauriUpdateModel.releaseUpdateOffer();
            }
            return;
          }
          // Recorded natively on EVERY check: only the owner of the state can
          // tell a re-offer apart. A retained offer keeps ITS OWN feed, the one
          // the install reuses, so the prefetch reads the channel it will.
          await dispatch.tauriUpdateModel.recordUpdateOffer({
            version: update.version,
            feed: retainedOffer?.feed ?? feed,
            userInitiated,
            checkToken,
          });
        } else {
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
          await dispatch.tauriUpdateModel.releaseUpdateOffer();
        }
      } catch (error) {
        logger.error('[updater] update check failed', {
          tag: 'updater_check_failed',
          ...updaterErrorContext(error),
          userInitiated,
        });
        // A check this one superseded may have accepted an offer and then
        // recorded nothing, because only the latest check may record. This
        // check has no manifest of its own, but the surviving offer carries
        // the version and feed its own check resolved and recording needs
        // neither a manifest nor a gate reading — so arm it here, on the path
        // THIS check read. Only while this check still owns the state: a newer
        // one answers for the offer.
        if (checkToken !== undefined && isLatestUpdateCheck(checkToken)) {
          await dispatch.tauriUpdateModel.armSurvivingOffer({
            userInitiated,
            checkToken,
            delivery,
          });
        }
        if (userInitiated) {
          toast({
            tone: 'destructive',
            title: formatMessageOutsideReact(updateFailureMessages.checkFailedTitle),
            description: formatMessageOutsideReact(updateFailureMessages.checkFailed),
          });
        }
      }
    },

    /**
     * Arm the prefetch for the offer left in state by a failed check, when
     * nothing answers for it yet. Both checks of an overlapping pair can finish
     * having recorded nothing — the older one because it was superseded inside
     * its registration, the newer one because its manifest failed — and the
     * cached path's prompt waits on a state event that would then never come.
     *
     * `delivery` is the path the FAILED check resolved, and it decides what the
     * recovery may do, never the path the surviving offer was carrying. That
     * offer came from an older gate reading, so acting on it is how a gate
     * turned off between the two checks still ends in cached work, which is
     * the one thing turning it off has to be able to stop.
     *
     * Recording is idempotent and takes no manifest, so this either arms the
     * offer or notes it as unarmed; either way the prompt becomes reachable.
     * Caller's job to check it still owns the state.
     */
    async armSurvivingOffer(payload: {
      userInitiated: boolean;
      checkToken: number;
      /** Undefined when the throw beat the gate reading, so no path is known. */
      delivery: UpdateDelivery | undefined;
    }) {
      const {
        version_to_update: offer,
        prefetch_state: state,
        prefetch_version: version,
        prefetch_region: region,
        prefetch_unarmed_version: unarmedVersion,
        prefetch_unarmed_region: unarmedRegion,
      } = store.getState().tauriUpdateModel;
      // A blocked feed is answered by nothing at all, prefetch included.
      if (!offer || offer.feed.kind === 'blocked') {
        return;
      }
      if (payload.delivery === 'legacy') {
        // The gate is off as of this check, so the offer takes the path it
        // resolved, exactly as a successful check would have rewritten it. The
        // prompt then opens on the offer alone and the click downloads; no
        // cached work is armed for a client the gate has just rescued.
        if (offer.delivery !== 'legacy') {
          dispatch.tauriUpdateModel.setVersionToUpdate({ ...offer, delivery: 'legacy' });
        }
        // Dropped only when the mirror says there is a native record to drop,
        // the same condition the successful legacy path releases on.
        if (store.getState().tauriUpdateModel.prefetch_state !== 'idle') {
          await dispatch.tauriUpdateModel.releaseUpdateOffer();
        }
        return;
      }
      // Only `cached` waits on a prefetch: `legacy` opens on the offer alone,
      // and `unresolved` opens on nothing until a check resolves its path.
      if (offer.delivery !== 'cached') {
        return;
      }
      // Already answered for: a native state naming this offer means a record
      // did land, and an unarmed note for it means the fallback is already live.
      const mirror = { state, version, region, unarmedVersion, unarmedRegion };
      if (stateDescribesOffer(mirror, offer) || unarmedDescribesOffer(mirror, offer)) {
        return;
      }
      if (payload.delivery === undefined) {
        // No gate reading, so this check cannot authorise cached work either.
        // The offer is left on its own path and noted as unarmed, which opens
        // the prompt on the fallback rather than on an event nobody will send.
        logger.warn('[updater] no resolved path for the surviving offer', {
          tag: 'updater_prefetch_skipped',
          reason: 'recovery_delivery_unresolved',
          version: offer.version,
        });
        dispatch.tauriUpdateModel.setPrefetchUnarmed({
          version: offer.version,
          region: prefetchRegionForFeed(offer.feed),
        });
        return;
      }
      logger.warn('[updater] arming the offer a superseded check left unarmed', {
        tag: 'updater_prefetch_rearmed',
        reason: 'superseded_check_failed',
        version: offer.version,
      });
      await dispatch.tauriUpdateModel.recordUpdateOffer({
        version: offer.version,
        feed: offer.feed,
        userInitiated: payload.userInitiated,
        checkToken: payload.checkToken,
      });
    },

    /**
     * Record the offered version natively and mirror what Rust publishes for it.
     * Subscribes BEFORE recording, or a warm readiness goes unheard. Every step
     * belongs to `checkToken`: superseded, it subscribes and records nothing.
     */
    async recordUpdateOffer(payload: {
      version: string;
      feed: UpdaterFeed;
      userInitiated: boolean;
      checkToken: number;
    }) {
      if (IS_HIDE_TAURI_UPDATER_ENABLED()) {
        // `reason` is what makes this line countable beside the native skips,
        // which all carry one.
        logger.info('[updater] updater hidden - skipping the prefetch', {
          tag: 'updater_prefetch_skipped',
          reason: 'updater_hidden',
          version: payload.version,
        });
        return;
      }

      // Logged with a `reason` like the skip above, so a superseded record is
      // countable rather than silent.
      const superseded = () => {
        if (isLatestUpdateCheck(payload.checkToken)) {
          return false;
        }
        logger.info('[updater] superseded check - skipping the prefetch', {
          tag: 'updater_prefetch_skipped',
          reason: 'superseded_check',
          version: payload.version,
        });
        return true;
      };

      if (superseded()) {
        return;
      }

      let subscribed = true;
      try {
        await listenToUpdaterPrefetchState((state) => {
          dispatch.tauriUpdateModel.applyPrefetchState(state);
        });
      } catch (error) {
        // No subscription is no mirror, so a readiness would go unheard. The
        // mirrored state is left as the native side last published it; what is
        // recorded is the renderer-only fact that this offer has no prefetch.
        logger.warn('[updater] could not subscribe to the prefetch state', {
          tag: 'updater_prefetch_failed',
          ...updaterErrorContext(error),
          version: payload.version,
        });
        subscribed = false;
      }

      // Re-read after the subscription's await: this is the window the whole
      // token exists for, since the native record is what starts a download.
      if (superseded()) {
        return;
      }
      if (!subscribed) {
        dispatch.tauriUpdateModel.setPrefetchUnarmed({
          version: payload.version,
          region: prefetchRegionForFeed(payload.feed),
        });
        return;
      }

      try {
        await recordOfferForFeed(payload.feed, payload.version, payload.userInitiated);
      } catch (error) {
        logger.warn('[updater] could not record the update offer', {
          tag: 'updater_prefetch_failed',
          ...updaterErrorContext(error),
          version: payload.version,
        });
        if (superseded()) {
          return;
        }
        dispatch.tauriUpdateModel.setPrefetchUnarmed({
          version: payload.version,
          region: prefetchRegionForFeed(payload.feed),
        });
      }
    },

    /**
     * Drop the offer the native side re-evaluates, because the channel offers
     * nothing or the version will never be installed. No manifest fetch and no
     * gate read, so it is safe wherever a check has already run.
     */
    async releaseUpdateOffer() {
      if (!isTauri()) {
        return;
      }
      try {
        await updaterBridge.clearUpdateOffer();
      } catch (error) {
        logger.warn('[updater] could not release the update offer', {
          tag: 'updater_offer_unreleased',
          ...updaterErrorContext(error),
        });
      }
    },

    async handleInstallTauriUpdate() {
      if (!isTauri()) {
        return;
      }
      const newUpdate = store.getState().tauriUpdateModel.version_to_update;
      if (!newUpdate) return;

      try {
        // Install via the SAME feed AND the same delivery path the check
        // resolved. Do NOT re-read either gate: a flip between check and
        // install must not reroute the feed, nor take a path no check resolved.

        // The prefetch duration is not sent: it belongs to the state Rust owns,
        // which the recorded route carries too. On the cached path Rust also
        // downloads the bundle itself whenever the cache cannot serve it.

        // Only a path resolved as `cached` reads the cache. An `unresolved`
        // offer installs the old way: no check here has read the gate for it,
        // and that path needs nothing on disk.
        const { installed, cached, prefetchMs } =
          newUpdate.delivery === 'cached'
            ? await installCachedForFeed(newUpdate.feed, newUpdate.version)
            : {
                installed: await installUpdateForFeed(newUpdate.feed, 'legacy'),
                cached: false,
                prefetchMs: undefined,
              };

        if (installed) {
          dispatch.tauriUpdateModel.setVersionToUpdate({
            ...newUpdate,
            isInstalled: true,
          });
          // `cached` is the route the native side took, not the readiness the
          // renderer saw: the two disagree whenever the bundle is deleted,
          // pruned or fails its signature re-check in between.
          trackTauriUpdate(newUpdate.version, { cached, prefetchMs });
          await dispatch.tauriUpdateModel.handleRelaunchApp();
        } else {
          // Not a failure: the feed no longer offers this version. On screen it
          // looks like a failed install, so it gets its own log signature
          // rather than leaving the banner to vanish unexplained.
          logger.warn('[updater] install found no update to apply', {
            tag: 'updater_install_no_update',
            version: newUpdate.version,
          });
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
        }
      } catch (error) {
        const errorCode = updaterErrorCode(error);
        if (errorCode && ALREADY_IN_FLIGHT_INSTALL_CODES.has(errorCode)) {
          logger.info('[updater] install deferred to work already in flight', {
            tag: 'updater_install_in_flight',
            errorCode,
            version: newUpdate.version,
          });
          return;
        }
        logger.error('[updater] install failed', {
          tag: 'updater_install_failed',
          ...updaterErrorContext(error),
          version: newUpdate.version,
        });
        // Retryable failures keep `version_to_update`: dropping it used to
        // remove the only way back to the button, leaving a manual reinstall.
        // A non-retryable one drops it, since retrying only fails again.
        if (errorCode && NON_RETRYABLE_INSTALL_CODES.has(errorCode)) {
          dispatch.tauriUpdateModel.setRejectedUpdate({
            version: newUpdate.version,
            errorCode,
          });
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
        }
        toast({
          tone: 'destructive',
          title: formatMessageOutsideReact(updateFailureMessages.installFailedTitle),
          description: formatMessageOutsideReact(installFailureBody(errorCode)),
        });
      }
    },

    /**
     * Report an install that completed without returning. A successful Windows
     * install exits the process inside `install`, so the route is recorded
     * natively and reported here, only for the version now running.
     */
    async reportCompletedUpdateInstall() {
      if (!isTauri()) {
        return;
      }
      try {
        const report = await updaterBridge.takeCompletedUpdateInstall();
        if (!report) return;
        trackTauriUpdate(report.version, {
          cached: report.cached,
          prefetchMs: report.prefetchMs,
        });
      } catch (error) {
        logger.warn('[updater] could not read the completed install route', {
          tag: 'updater_install_route_unread',
          ...updaterErrorContext(error),
        });
      }
    },

    async handleRelaunchApp() {
      if (!isTauri()) {
        return;
      }
      try {
        trackTauriRelaunch();
        await relaunch();
      } catch (error) {
        trackTauriFailedToRelaunch(String(error));
        logger.error('Failed to relaunch app', updaterErrorContext(error));
      } finally {
        const newUpdate = store.getState().tauriUpdateModel.version_to_update;
        if (newUpdate) {
          dispatch.tauriUpdateModel.setVersionToUpdate({
            ...newUpdate,
            popup_dismissed: true,
          });
        }
      }
    },
  }),
});
